import { actionTypes } from "./action-types";
import { BASE_URL, debugLogToConsole, makeIsoDateString, parseDateFromIsoString } from "../utils";

const TODAY = new Date();

/* ************************************************************************ */
/* SCHEDULE PAGE                                                            */
/* ************************************************************************ */

export function setSelectedPractice(idx) {
  return {
    type: actionTypes.setSelectedPractice,
    payload: idx,
  };
}

export function setSelectedCaseBlockDate(year, month, date) {
  return {
    type: actionTypes.setSelectedCaseBlockDate,
    payload: {
      year,
      month,
      date
    }
  };
}

// Reset active case and patient data when schedule dashboard is closed
export function collapseReload() {
  return {
    type: actionTypes.collapseReload
  };
}

/* ************************************************************************ */
/* CASE SCHEDULE                                                            */
/* ************************************************************************ */

export function setCaseTabFlag(value) {
  return {
    type: actionTypes.setCaseTabFlag,
    payload: value,
  };
}

export function setOpMode(value) {
  return {
    type: actionTypes.setOpMode,
    payload: value,
  };
}

export function setLoadingCaseData(value) {
  return {
    type: actionTypes.setLoadingCaseData,
    payload: value,
  };
}

export function shouldResyncCaseData(value) {
  return {
    type: actionTypes.shouldResyncCaseData,
    payload: value,
  };
}

export function resetCaseBlockLinkTimeFlag() {
  return {
    type: actionTypes.resetCaseBlockLinkTimeFlag
  };
}

/* ************************************************************************ */
/* NEW CASE                                                                 */
/* ************************************************************************ */

export function initiateNewCaseSchedule(facData) {
  return {
    type: actionTypes.initiateNewCaseSchedule,
    payload: { ...facData }
  };
}

export function toggleNewCaseScheduleWarningDialog1(value) {
  return {
    type: actionTypes.toggleNewCaseScheduleWarningDialog1,
    payload: value,
  };
}

export function toggleNewCaseScheduleWarningDialog2(value) {
  return {
    type: actionTypes.toggleNewCaseScheduleWarningDialog2,
    payload: value,
  };
}

export function updateNewCaseScheduleData(newData) {
  return {
    type: actionTypes.updateNewCaseScheduleData,
    payload: newData,
  };
}

export function createNewCaseSchedule(caseData, selectedPractice) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    reqHeaders.append(
      "Authorization",
      `Bearer ${localStorage.getItem("token")}`,
    );

    if (caseData.scheduleStartDate) {
      const { year, month, date } = parseDateFromIsoString(caseData.scheduleStartDate);
      caseData.scheduleStartDate = makeIsoDateString(year, month, date);
    }

    if (caseData.scheduleEndDate) {
      const { year, month, date } = parseDateFromIsoString(caseData.scheduleEndDate);
      caseData.scheduleEndDate = makeIsoDateString(year, month, date);
    }

    const formData = new FormData();
    formData.append("caseData", JSON.stringify(caseData));
    formData.append('practiceNpi', selectedPractice.npi);

    const requestOptions = {
      method: "POST",
      headers: reqHeaders,
      body: formData,
      redirect: "follow",
    };

    fetch(`${BASE_URL}/casesSchedule/scheduleCase`, requestOptions)
      .then((response) => response.text())
      .then((result) => {
        const data = JSON.parse(result);
        dispatch({
          type: actionTypes.createNewCaseSchedule,
          payload: data,
        });
      })
      .catch((err) => {
        debugLogToConsole(err);
      });
  };
}

/* ************************************************************************ */
/* EXISTING CASE SCHEDULE                                                   */
/* ************************************************************************ */

export function clearCaseSchedulesData() {
  return {
    type: actionTypes.clearCaseSchedulesData
  };
};

export function getCaseSchedulesData(selectedPractice, scheduleDate, scheduleMonth, scheduleYear, scheduleEndDate, scheduleEndMonth, scheduleEndYear, currentPage) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const formData = new FormData();
    formData.append('currentPage', currentPage)
    if (selectedPractice && Object.keys(selectedPractice).length > 0) {
      formData.append('practiceNpi', selectedPractice.npi);
    }
    if (scheduleDate) {
      formData.append('scheduleDate', scheduleDate);
    }
    if (scheduleMonth !== undefined) {
      formData.append('scheduleMonth', scheduleMonth);
    }
    if (scheduleYear) {
      formData.append('scheduleYear', scheduleYear);
    }

    if (scheduleEndDate) {
      formData.append('scheduleEndDate', scheduleEndDate);
    }
    if (scheduleEndMonth !== undefined) {
      formData.append('scheduleEndMonth', scheduleEndMonth);
    }
    if (scheduleEndYear) {
      formData.append('scheduleEndYear', scheduleEndYear);
    }

    const requestOptions = {
      method: "POST",
      body: formData,
      headers: reqHeaders,
      redirect: "follow"
    };

    fetch(
      `${BASE_URL}/casesSchedule/getCaseSchedulesAndRelatedData`,
      requestOptions,
    )
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        const {
          success,
          caseSchedules,
          caseSchedulesCount,
          facilities,
          userPractices,
          selectedPracticeNpi
        } = data;

        dispatch({
          type: actionTypes.getCaseSchedulesData,
          payload: {
            success,
            caseSchedules,
            caseSchedulesCount,
            facilities,
            userPractices,
            selectedPracticeNpi
          },
        });
      })
      .catch((err) => {
        debugLogToConsole(err);
      });
  };
}

export function loadCaseSchedule(scheduleId) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const formData = new FormData();
    formData.append("scheduleId", scheduleId);

    const requestOptions = {
      method: "POST",
      body: formData,
      headers: reqHeaders,
      redirect: "follow",
    };

    fetch(`${BASE_URL}/casesSchedule/getCaseScheduleData`, requestOptions)
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        const { success, patientData, caseScheduleData } = data;

        dispatch({
          type: actionTypes.loadCaseSchedule,
          payload: {
            success,
            patientData,
            caseScheduleData,
          },
        });
      })
      .catch((err) => {
        debugLogToConsole(err);
      });
  };
}

export function toggleCaseLoader(value) {
  return {
    type: actionTypes.toggleCaseLoader,
    payload: value
  };
}

// Update active case schedule
export function updateCaseSchedule(newData) {
  return {
    type: actionTypes.updateCaseSchedule,
    payload: newData,
  };
}

export function getCaseSchedulePatientData(patientId) {
  return async (dispatch) => {
    debugLogToConsole(`Getting data for patient id = ${patientId}`);
    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
    const params = { id: patientId };
    url.search = new URLSearchParams(params).toString();

    const requestOptions = {
      method: "GET",
      headers: reqHeaders,
      redirect: "follow",
    };

    fetch(url, requestOptions)
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        debugLogToConsole(data);
        dispatch({
          type: actionTypes.getCaseSchedulePatientData,
          payload: data.patient,
        });
      })
      .catch((error) => debugLogToConsole("error", error));
  };
}

export function saveCaseSchedule(caseData) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    reqHeaders.append(
      "Authorization",
      `Bearer ${localStorage.getItem("token")}`,
    );

    const formData = new FormData();
    // Sending case data as a file to bypass http request pararms size limit
    formData.append('json', new Blob([JSON.stringify(caseData)], { type: 'application/json' }));

    const requestOptions = {
      method: "POST",
      headers: reqHeaders,
      body: formData,
      redirect: "follow",
    };

    fetch(`${BASE_URL}/casesSchedule/updateCaseSchedule`, requestOptions)
      .then((response) => response.text())
      .then((result) => {
        const data = JSON.parse(result);
        //  Deleted this line from the payload object -> reloadCaseId: caseData._id
        dispatch({
          type: actionTypes.saveCaseSchedule,
          payload: caseData
        });
      })
      .catch((err) => {
        debugLogToConsole(err);
      });
  };
}

/* ************************************************************************ */
/* CASE BLOCKS                                                              */
/* ************************************************************************ */

export function toggleCbLoader(value) {
  return {
    type: actionTypes.toggleCbLoader,
    payload: value
  };
}

export function toggleScheduleCaseDrawer(value) {
  return {
    type: actionTypes.toggleScheduleCaseDrawer,
    payload: value
  };
}

export function toggleCaseBlockDrawer(value) {
  return {
    type: actionTypes.toggleCaseBlockDrawer,
    payload: value
  };
}

export function loadCaseBlocksData(data) {
  return async (dispatch) => {
    const { practiceNpi, scheduleDate, scheduleMonth, scheduleYear } = data;

    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const formData = new FormData();
    formData.append("practiceNpi", practiceNpi);
    formData.append("scheduleDate", scheduleDate);
    formData.append("scheduleMonth", scheduleMonth);
    formData.append("scheduleYear", scheduleYear);

    const requestOptions = {
      method: "POST",
      headers: reqHeaders,
      redirect: "follow",
      body: formData,
    };

    fetch(`${BASE_URL}/caseBlock/getCaseBlocksAndRelatedData`, requestOptions)
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        const {
          success,
          facilities,
          assignments,
          caseBlocks,
          patientsData,
          orphanedCases,
          directorBlocks,
          shiftBlocks,
          callBlocks,
        } = data;

        dispatch({
          type: actionTypes.loadCaseBlocksData,
          payload: {
            success,
            facilities,
            assignments,
            caseBlocks,
            patientsData,
            orphanedCases,
            directorBlocks,
            shiftBlocks,
            callBlocks,
          },
        });
      })
      .catch((err) => {
        debugLogToConsole(err);
      });
  };
}

export function editCaseBlock(cbId, cbCases = [], patientData = {}) {
  return {
    type: actionTypes.editCaseBlock,
    payload: {
      cbId,
      cbCases,
      patientData
    }
  };
}

export function updateCaseBlockData(data) {
  return {
    type: actionTypes.updateCaseBlockData,
    payload: data
  };
}

export function saveCaseBlock({ facilityData, casesToCreate, casesToDeleteIds, blockData }) {
  return async (dispatch) => {
    const formData = new FormData();

    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const reqOptions = {
      method: 'POST',
      headers: reqHeaders,
      body: formData,
      redirect: 'follow'
    };
    const {
      _id,
      date,
      isNoneCrna,
      crna,
      isNoneAnesthesiologist,
      anesthesiologist,
      surgeon,
      isLinked,
      linkedStartDate,
      linkedStartHour,
      linkedStartMinute,
      linkedEndHour,
      linkedEndDate,
      linkedEndMinute,
      blockStartDate,
      blockStartHour,
      blockStartMinute,
      blockEndDate,
      blockEndHour,
      blockEndMinute
    } = blockData;

    formData.append('blockId', _id);
    formData.append('facilityData', JSON.stringify(facilityData));
    formData.append('casesToCreate', JSON.stringify(casesToCreate));
    formData.append('deleteCaseIds', JSON.stringify(casesToDeleteIds));

    // Block providers
    formData.append('isNoneCrna', JSON.stringify(isNoneCrna));
    formData.append('isNoneAnesthesiologist', JSON.stringify(isNoneAnesthesiologist));
    formData.append('crna', JSON.stringify(crna));
    formData.append('anesthesiologist', JSON.stringify(anesthesiologist));
    formData.append('surgeon', JSON.stringify(surgeon));

    // Block date and times
    formData.append('date', date);
    formData.append('isLinked', isLinked);
    if (isLinked) {
      formData.append("linkedStartDate", linkedStartDate);
      formData.append("linkedStartHour", linkedStartHour);
      formData.append("linkedStartMinute", linkedStartMinute);
      formData.append("linkedEndDate", linkedEndDate);
      formData.append("linkedEndHour", linkedEndHour);
      formData.append("linkedEndMinute", linkedEndMinute);
    } else {
      formData.append('blockStartDate', blockStartDate);
      formData.append('blockStartHour', blockStartHour);
      formData.append('blockStartMinute', blockStartMinute);
      formData.append('blockEndDate', blockEndDate);
      formData.append('blockEndHour', blockEndHour);
      formData.append('blockEndMinute', blockEndMinute);
    }

    fetch(`${BASE_URL}/caseBlock/updateCaseBlock`, reqOptions)
      .then((response) => response.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.saveCaseBlock,
            payload: data
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };
}

export function setEditCaseBlockCases(cases) {
  return {
    type: actionTypes.setEditCaseBlockCases,
    payload: cases
  };
}

export function addPatientToCaseBlock(patient) {
  return {
    type: actionTypes.addPatientToCaseBlock,
    payload: patient
  };
}

export function reloadCaseBlockCasePatientData(patientId) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "GET",
      headers: reqHeaders,
      redirect: "follow",
    };

    const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
    const params = { id: patientId };
    url.search = new URLSearchParams(params).toString();

    fetch(url, requestOptions)
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.reloadCaseBlockCasePatientData,
            payload: data.patient
          });
        }
      })
      .catch((error) => debugLogToConsole("error", error));
  };
}

export function deletePatientFromCaseBlock(patientId) {
  return {
    type: actionTypes.deletePatientFromCaseBlock,
    payload: patientId
  };
}

// Placeholder for updateCaseBlockFilterDate

export function resetCaseBlocksState() {
  return {
    type: actionTypes.resetCaseBlocksState,
  };
}

export function updateCaseBlockAnesthesiologist(cbId, anesthesiologist) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('caseBlockId', cbId);
    formData.append('anes', JSON.stringify({
      addedPractices: [],
      avatarImageUrl: anesthesiologist.avatarImageUrl,
      icon: anesthesiologist.avatarImageUrl,
      caseRelation: anesthesiologist.specialty,
      credentials: anesthesiologist.credentials,
      fullName: `${anesthesiologist.lastName}, ${anesthesiologist.firstName}`,
      npi: anesthesiologist.npi,
      text: `${anesthesiologist.lastName}, ${anesthesiologist.firstName}`,
      user: anesthesiologist._id
    }));

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    fetch(`${BASE_URL}/caseBlock/syncAnesthesiologist`, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.updateCaseBlockAnesthesiologist
          });
        }
      })
      .catch(error => console.log('error', error));
  };
}

export function updateCaseBlockCrna(cbId, crna) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem('token');
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const formData = new FormData();
    formData.append('caseBlockId', cbId);
    formData.append('crna', JSON.stringify({
      addedPractices: [],
      avatarImageUrl: crna.avatarImageUrl,
      icon: crna.avatarImageUrl,
      caseRelation: crna.specialty,
      credentials: crna.credentials,
      fullName: `${crna.lastName}, ${crna.firstName}`,
      npi: crna.npi,
      text: `${crna.lastName}, ${crna.firstName}`,
      user: crna._id
    }));

    const requestOptions = {
      method: 'POST',
      body: formData,
      redirect: 'follow',
      headers: reqHeaders
    };
    fetch(`${BASE_URL}/caseBlock/syncCrna`, requestOptions)
      .then(res => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.updateCaseBlockCrna
          });
        }
      })
      .catch(error => console.log('error', error));
  };
}

export function getProvidersSchedule(data) {
  return async (dispatch) => {
    const { pUsers, scheduleDate, scheduleMonth, scheduleYear } = data;

    if (pUsers) {
      const npis = pUsers.map((el) => el.npi);

      const formData = new FormData();
      formData.append("npis", JSON.stringify(npis));
      formData.append("scheduleDate", scheduleDate);
      formData.append("scheduleMonth", scheduleMonth);
      formData.append("scheduleYear", scheduleYear);

      const reqHeaders = new Headers();
      const token = localStorage.getItem("token");
      reqHeaders.append("Authorization", `Bearer ${token}`);

      const requestOptions = {
        method: "POST",
        body: formData,
        redirect: "follow",
        headers: reqHeaders,
      };

      fetch(`${BASE_URL}/caseBlock/getProviderTimes`, requestOptions)
        .then((res) => res.text())
        .then((result) => {
          const data = JSON.parse(result);
          dispatch({
            type: actionTypes.getProvidersSchedule,
            payload: data,
          });
        })
        .catch((err) => {
          console.log(err);
        });
    }
  };
}

export function addDirectorBlock(data) {
  return async (dispatch) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem("token");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formData = new FormData();
    formData.append("directorBlockData", JSON.stringify(data));
    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: formData,
      redirect: "follow",
    };

    fetch(`${BASE_URL}/directorBlock/addDirectorBlock`, requestOptions)
      .then((response) => response.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          console.log("Director block added!");
          // getProviderCaseTimes();
          // getData();
        }

        // setShowDirectorBlockDrawer(false);
      })
      .catch((err) => console.log(err));
  };
}

// Placeholder for updateDirectorBlock
export function addShiftBlock(data) {
  return async (dispatch) => { };
}

// Placeholder for updateShiftBlock

export function addCallBlock(data) {
  return async (dispatch) => { };
}

// Placeholder for updateCallBlock

export function saveFacilityColors(bgColor, textColor, practiceId, facilityId) {
  return async (dispatch) => {
    const formData = new FormData();

    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const reqOptions = {
      method: 'POST',
      headers: reqHeaders,
      body: formData,
      redirect: 'follow'
    };

    formData.append('practiceId', practiceId);
    formData.append('facilityId', facilityId);
    formData.append('bgColor', bgColor);
    formData.append('textColor', textColor);

    fetch(`${BASE_URL}/practice/savePracticeFacilityColors`, reqOptions)
      .then((response) => response.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.saveFacilityColors,
            payload: {
              bgColor,
              textColor,
              practiceId,
              facilityId
            }
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };
}

/* ************************************************************************ */
/* NEW CASEBLOCK CASE                                                       */
/* ************************************************************************ */

export function initiateNewCaseBlock(initCaseBlockData) {
  return {
    type: actionTypes.initiateNewCaseBlock,
    payload: initCaseBlockData
  };
}

export function setCaseBlockCaseInitialData(initData) {
  return {
    type: actionTypes.setCaseBlockCaseInitialData,
    payload: initData
  };
}

export function createNewCaseBlock({ practiceNpi, facilityData, casesToCreate, blockData }) {
  return async (dispatch) => {
    const formData = new FormData();

    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append('Authorization', `Bearer ${token}`);

    const reqOptions = {
      method: 'POST',
      headers: reqHeaders,
      body: formData,
      redirect: 'follow'
    };

    const {
      date,
      isNoneCrna,
      crna,
      isNoneAnesthesiologist,
      anesthesiologist,
      surgeon,
      isLinked,
      linkedStartDate,
      linkedStartHour,
      linkedStartMinute,
      linkedEndHour,
      linkedEndDate,
      linkedEndMinute,
      blockStartDate,
      blockStartHour,
      blockStartMinute,
      blockEndDate,
      blockEndHour,
      blockEndMinute
    } = blockData;

    formData.append('practiceNpi', practiceNpi);
    formData.append('facilityData', JSON.stringify(facilityData));
    formData.append('casesToCreate', JSON.stringify(casesToCreate));

    // Block providers
    formData.append('isNoneCrna', JSON.stringify(isNoneCrna));
    formData.append('isNoneAnesthesiologist', JSON.stringify(isNoneAnesthesiologist));
    formData.append('crna', JSON.stringify(crna));
    formData.append('anesthesiologist', JSON.stringify(anesthesiologist));
    formData.append('surgeon', JSON.stringify(surgeon));

    // Block date and times
    formData.append('date', date);
    formData.append('isLinked', isLinked);
    if (isLinked) {
      formData.append("linkedStartDate", linkedStartDate);
      formData.append("linkedStartHour", linkedStartHour);
      formData.append("linkedStartMinute", linkedStartMinute);
      formData.append("linkedEndDate", linkedEndDate);
      formData.append("linkedEndHour", linkedEndHour);
      formData.append("linkedEndMinute", linkedEndMinute);
    } else {
      formData.append('blockStartDate', blockStartDate);
      formData.append('blockStartHour', blockStartHour);
      formData.append('blockStartMinute', blockStartMinute);
      formData.append('blockEndDate', blockEndDate);
      formData.append('blockEndHour', blockEndHour);
      formData.append('blockEndMinute', blockEndMinute);
    }

    fetch(`${BASE_URL}/caseBlock/createCaseBlockAndAddCases`, reqOptions)
      .then((response) => response.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.createNewCaseBlock,
            payload: data
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };
}

export function setCaseBlockActiveCaseSchedule(caseId) {
  return {
    type: actionTypes.setCaseBlockActiveCaseSchedule,
    payload: caseId
  };
}

export function toggleCbCaseScheduleDashboard(value, caseId) {
  return {
    type: actionTypes.toggleCbCaseScheduleDashboard,
    payload: {
      value,
      caseId
    }
  };
}

export function updateCaseBlockActiveCaseSchedule(newData) {
  return {
    type: actionTypes.updateCaseBlockActiveCaseSchedule,
    payload: newData
  };
}

// Placeholder for setActiveCaseBlockCase

export function markCaseBlockCaseForCreation() {
  return {
    type: actionTypes.markCaseBlockCaseForCreation
  };
}

export function editCaseBlockCaseMarkedForCreation (caseData) {
  return {
    type: actionTypes.editCaseBlockCaseMarkedForCreation,
    payload: caseData
  };
}

export function updateCaseBlockCaseMarkedForCreation() {
  return {
    type: actionTypes.updateCaseBlockCaseMarkedForCreation
  };
}

export function markCaseForUpdate() {
  return {
    type: actionTypes.markCaseForUpdate
  };
}

export function unMarkCaseBlockCaseForCreation(caseIdx) {
  return {
    type: actionTypes.unMarkCaseBlockCaseForCreation,
    payload: caseIdx
  };
}

export function markCaseBlockCaseForDeletion(caseId) {
  return {
    type: actionTypes.markCaseBlockCaseForDeletion,
    payload: caseId
  };
}

export function unMarkCaseBlockCaseForDeletion(caseId) {
  return {
    type: actionTypes.unMarkCaseBlockCaseForDeletion,
    payload: caseId
  };
}

/* ************************************************************************ */
/* EXISTING CASE BLOCK CASES                                                */
/* ************************************************************************ */

// Placeholder for setExistingCaseBlockCases

/* ************************************************************************ */
/* NEW CASE SCHEDULE PATIENT                                                */
/* ************************************************************************ */

export function addPatientToNewCaseSchedule(patient) {
  return {
    type: actionTypes.addPatientToNewCaseSchedule,
    payload: patient,
  };
}

export function updatePatientToNewCaseSchedule(patient) {
  return {
    type: actionTypes.updatePatientToNewCaseSchedule,
    payload: patient,
  };
}

export function deletePatientFromNewCaseSchedule() {
  return {
    type: actionTypes.deletePatientFromNewCaseSchedule,
  };
}

export function reloadCaseSchedulePatientData(patientId) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "GET",
      headers: reqHeaders,
      redirect: "follow",
    };

    const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
    const params = { id: patientId };
    url.search = new URLSearchParams(params).toString();

    fetch(url, requestOptions)
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.reloadCaseSchedulePatientData,
            payload: data.patient,
          });
        }
      })
      .catch((error) => debugLogToConsole("error", error));
  };
}

/* ************************************************************************ */
/* EXISTING CASE SCHEDULE PATIENT                                           */
/* ************************************************************************ */

export function addPatientToExistingCaseSchedule(patient) {
  return {
    type: actionTypes.addPatientToExistingCaseSchedule,
    payload: patient,
  };
}

export function updatePatientToExistingCaseSchedule(patient) {
  return {
    type: actionTypes.updatePatientToExistingCaseSchedule,
    payload: patient,
  };
}

export function deletePatientFromCaseSchedule() {
  return {
    type: actionTypes.deletePatientFromCaseSchedule,
  };
}

export function reloadExistingCaseSchedulePatientData(patientId) {
  return async (dispatch) => {
    const reqHeaders = new Headers();
    const token = localStorage.getItem("token");
    reqHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "GET",
      headers: reqHeaders,
      redirect: "follow",
    };

    const url = new URL(`${BASE_URL}/patients/getPatientProfile`);
    const params = { id: patientId };
    url.search = new URLSearchParams(params).toString();

    fetch(url, requestOptions)
      .then((res) => res.text())
      .then((result) => {
        const data = JSON.parse(result);
        if (data.success) {
          dispatch({
            type: actionTypes.reloadExistingCaseSchedulePatientData,
            payload: data.patient,
          });
        }
      })
      .catch((error) => debugLogToConsole("error", error));
  };
}

export function setCaseScheduleDateRangeFilterStartDate(year, month, date) {
  return {
    type: actionTypes.setCaseScheduleDateRangeFilterStartDate,
    payload: {
      year,
      month,
      date
    }
  };
}

export function setCaseScheduleDateRangeFilterEndDate(year, month, date) {
  return {
    type: actionTypes.setCaseScheduleDateRangeFilterEndDate,
    payload: {
      year,
      month,
      date
    }
  };
}

export function setShouldSaveCaseData(value) {
  return {
    type: actionTypes.setShouldSaveCaseData,
    payload: value
  };
}
